import { Component, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionsSubject, Store } from '@ngrx/store';
import { DataBreachProcessService } from '@pt/api-client';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IoniconsOutline, LoggedUser, ND } from '@pt/pt-models';
import {
  ListDataBreachNoteResponse
} from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ListDataBreachNoteResponse';
import { Note } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/listdatabreachnoteresponse/Note';
import { CreateNoteRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/CreateNoteRequest';
import { PtCoreSelectors } from '@pt/pt-core';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'pt-create-data-breach-note-dialog',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title'>
        {{ "TITLE.NOTES" | translate }}
      </h4>
    </div>
    <div class='modal-body'>

      <div class="note-container">

        <div class="d-flex align-items-center justify-content-center w-100 h-100" *ngIf="loading">
          <div class="spinner-border" role="status">
            <span class="sr-only"></span>
          </div>
        </div>


        <ng-container *ngIf="!loading">
          <div *ngIf="notes.length === 0" class="d-flex justify-content-center align-items-center w-100 h-100">
            {{ 'MESSAGE.NODATA' | translate }}
          </div>
          <div class="note" *ngFor="let n of notes">
            <div class="header">
              <b>{{ n.authorFullName }} {{ n.createdAt | localizedDate: { pattern: dateFormat, defaultValue: ND } }}</b>
              <button type='button'
                      container='body'
                      ngbTooltip="{{'TOOLTIP.DELETE_NOTE' | translate}}"
                      class='btn btn-sm ms-1 btn-delete'
                      *ngIf="loggedUserId === n.authorUserId"
                      (click)='deleteNote(n)'>
                <pt-icon [key]='IconDelete'></pt-icon>
              </button>
            </div>
            <div class="body">
              {{ n.note }}
            </div>
          </div>
        </ng-container>

      </div>

      <div class="create-note">
        <form [formGroup]="formGroup" class="mb-0">
          <div class="d-flex justify-content-between">
            <pt-input-text-area
              style="height: 80px; min-height: 80px;"
              [disabled]="loading"
              class="w-100"
              [minRow]="4"
              [inputId]='"note"'
              formControlName='note'
            ></pt-input-text-area>
            <button type='button' class='btn btn-outline-secondary btn-sm'
                    style="height: 80px; margin-left: 10px;"
                    [disabled]="formGroup.invalid"
                    (click)='sendNote()'>
              {{ 'BUTTON.SEND' | translate }}
            </button>
          </div>

        </form>
      </div>
    </div>

    <div class='modal-footer'>
      <button type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(false)'>
        {{ 'BUTTON.CLOSE' | translate }}
      </button>
    </div>
  `,
  styles: [`
    .note-container {
      height: 300px;
      overflow-y: auto;
      border-bottom: 1px solid #33333333;
      margin-bottom: 10px;
      padding-bottom: 10px;
    }

    .note-container::-webkit-scrollbar {
      width: 5px;
      height: 5px;
    }

    .note-container::-webkit-scrollbar-thumb {
      background: var(--bs-primary);
      border-radius: 0;
    }

    .note-container::-webkit-scrollbar-thumb:hover {
      background: var(--bs-secondary);
    }

    .note-container::-webkit-scrollbar-track {
      background: #F0F0F0;
      border-radius: 0;
      box-shadow: inset 0 0 0 0 #F0F0F0;
    }

    .btn-delete:hover{
      color: #aa000033;
    }

    .btn-delete:hover{
      color: #aa0000;
    }

    .note {
      display: flex;
      flex-direction: column;
      margin-bottom: 10px;
      padding: 5px 10px;
      border: 1px solid #33333333;
    }

    .note .header {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
    }

    .note .body {

    }
  `]
})
export class CreateDataBreachNoteDialogComponent implements OnDestroy {

  public loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);
  public loggedUserId?: number;

  public loading = false;
  public formGroup: FormGroup = new FormGroup({
    note: new FormControl(null, [Validators.required])
  })
  public notes: Note[] = [];
  public dateFormat = '(dd/MM/yyyy HH:mm)'
  public ND = ND;
  public IconDelete = IoniconsOutline.trash;
  private dataBreachId?: number;

  private destroy$ = new Subject();

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private dataBreachProcessService: DataBreachProcessService
  ) {

    this.loginResponse$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((loginResponse: LoggedUser) => {
      this.loggedUserId = loginResponse?.id;
    })

  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
  }

  run(dataBreachId: number): void {
    this.dataBreachId = dataBreachId;
    this.loadNote();
  }

  sendNote() {
    if (this.formGroup.valid && this.dataBreachId) {
      const note: string = this.formGroup.controls.note.value;
      const request: CreateNoteRequest = {
        note
      };
      this.dataBreachProcessService.createNotes(this.dataBreachId, request).subscribe(() => {
        this.loadNote();
      }, () => {
        this.loadNote();
      })
    }
  }

  deleteNote(n: Note) {
    this.loading = true;
    this.dataBreachProcessService.deleteNote(this.dataBreachId, n.id).subscribe(() => {
      this.loadNote();
    }, () => {
      this.loadNote();
    })
  }

  private loadNote() {
    if (this.dataBreachId) {
      this.formGroup.reset();
      this.dataBreachProcessService.getNotes(this.dataBreachId).subscribe((response: ListDataBreachNoteResponse) => {
        this.notes = response.notes;
        this.loading = false
      }, () => {
        this.notes = []
        this.loading = false
      })
    }

  }
}
